import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ScanLine,
    TrendingUp,
    Brain,
    Gauge,
    Sparkles,
    Globe2,
    CalendarClock,
    Beaker,
    Target,
    Activity,
    ArrowUpRight,
} from "lucide-react";
import { ASSETS } from "../lib/constants";
import Slopulation from "./Slopulation";

const FEATURES = [
    {
        icon: Beaker,
        t: "My Bloodwork Hub",
        d: "Order, upload, generate report, book a consult. The full cycle — clearly laid out so nothing gets skipped.",
    },
    {
        icon: ScanLine,
        t: "Automated OCR Scanning",
        d: "Upload results directly. Markers, units, and collection dates are extracted automatically. Zero manual entry.",
    },
    {
        icon: Target,
        t: "Enhanced Reference Ranges",
        d: "Your markers are evaluated and scored against TRT calibrated ranges that actually fit your context - not general population sub-optimal averages.",
        linkTo: "/engine#schematic",
        linkLabel: "See the engine",
    },
    {
        icon: Activity,
        t: "Biomarker Score & Ratios",
        d: "One composite score that weighs each of your markers by clinical importance. Reads the relationships between markers and key hormone ratios, not just isolated numbers.",
        linkTo: "/engine#schematic",
        linkLabel: "See the engine",
    },
    {
        icon: Sparkles,
        t: "My Free T",
        d: "Four methods: Vermeulen, Mazer, Södergård, Zakharov — accurate even at supra-physiological levels.",
        linkTo: "/engine#formulas",
        linkLabel: "See the formulas",
    },
    {
        icon: Gauge,
        t: "The Testimator™",
        d: "Predict T levels across different protocols before you run them. Anchored to your actual Total T.",
        linkTo: "#testimator",
        linkLabel: "See the Testimator",
    },
    {
        icon: TrendingUp,
        t: "High / Low Indicators",
        d: "Every biomarker shows at a glance whether it's running high or low against TRT-optimized reference ranges.",
    },
    {
        icon: Globe2,
        t: "International Unit Conversion",
        d: "Convert between units across every marker, for all regions (ng/dL, nmol/L, pg/mL, etc).",
    },
    {
        icon: CalendarClock,
        t: "Bloodwork Reminders",
        d: "Recommended retesting timeframes based on your last results and TRT best practices. Stay on cycle.",
    },
    {
        icon: Brain,
        t: "AI Bloodwork Report",
        d: "Your active protocol + biomarker snapshot are interpreted together in context — not in isolation, not as generic numbers.",
    },
];

const REPORT_SECTIONS = [
    "Recommendations",
    "Protocol & Dose Response",
    "Hormonal Balance",
    "Muscle-Building Potential",
    "Compound Analysis",
    "Healthspan Assessment",
    "Cardiovascular Risk Profile",
    "Overall Health",
];

export default function MyBloodwork() {
    return (
        <section
            id="bloodwork"
            data-testid="bloodwork-section"
            className="relative py-24 lg:py-32 bg-black overflow-hidden border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-14 lg:mb-20">
                    <div>
                        <div className="eyebrow mb-6">
                            <span className="dot" /> My Bloodwork
                        </div>
                        <h2 className="font-display font-bold clamp-h2 text-white">
                            Performance bloodwork.
                            <br />
                            <span className="blue-glow">
                                Ranges built for you.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:pt-4 space-y-4 text-white/65 text-base lg:text-lg leading-relaxed">
                        <p>
                            Standard reference ranges were built for the{" "}
                            <Slopulation />. For someone optimizing on TRT,
                            that context is wrong. TRT Plus uses enhanced
                            reference ranges informed by deep analysis of
                            thousands of real-world cases.
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 items-start">
                    {/* Features grid */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        {FEATURES.map((f, i) => {
                            const content = (
                                <>
                                    {f.linkTo && (
                                        <ArrowUpRight
                                            size={14}
                                            className="bloodwork-card-corner"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <div className="w-10 h-10 rounded-lg bg-[#00e6a0]/10 border border-[#00e6a0]/25 flex items-center justify-center mb-4">
                                        <f.icon
                                            size={18}
                                            className="text-[#00e6a0]"
                                        />
                                    </div>
                                    <h3 className="font-display font-semibold text-white text-[17px] mb-1.5">
                                        {f.t}
                                    </h3>
                                    <p className="text-sm text-white/55 leading-relaxed">
                                        {f.d}
                                    </p>
                                    {f.linkTo && (
                                        <div className="bloodwork-card-link mt-4 pt-3 border-t border-white/5 text-[10.5px] font-mono uppercase tracking-[0.18em] flex items-center gap-1.5">
                                            <span className="text-[#00E6A0]">
                                                {">"}
                                            </span>
                                            <span className="text-white/55 group-hover:text-white transition-colors">
                                                {f.linkLabel}
                                            </span>
                                            <span className="text-[#49BEF5]">
                                                →
                                            </span>
                                        </div>
                                    )}
                                </>
                            );

                            const sharedProps = {
                                className: `tile p-5 relative block ${f.linkTo ? "group bloodwork-card-linked cursor-pointer" : ""}`,
                                "data-testid": `bloodwork-feature-${f.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
                            };

                            return (
                                <motion.div
                                    key={f.t}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                                >
                                    {f.linkTo ? (
                                        <Link to={f.linkTo} {...sharedProps}>
                                            {content}
                                        </Link>
                                    ) : (
                                        <div {...sharedProps}>{content}</div>
                                    )}
                                </motion.div>
                            );
                        })}

                        {/* AI Report detail card */}
                        <div className="tile p-6 sm:col-span-2 relative overflow-hidden">
                            <div
                                className="absolute -right-10 -top-10 w-64 h-64 rounded-full pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(circle, rgba(43,166,224,0.15), transparent 60%)",
                                }}
                            />
                            <div className="relative">
                                <div className="text-[11px] uppercase tracking-widest font-mono text-[#49bef5] mb-3">
                                    // 8 Sections · Generated Per Run
                                </div>

                                {/* Input pipeline: LABS + ACTIVE PROTOCOL → AI REPORT */}
                                <div className="flex flex-wrap items-center gap-2 mb-5">
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-mono text-white/80">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#49bef5]" />
                                        Labs
                                    </span>
                                    <span className="text-white/25 text-sm font-mono">·</span>
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-mono text-white/80">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#49bef5]" />
                                        Active Protocol
                                    </span>
                                    <span className="text-[#49bef5]/70 text-sm font-mono mx-1">→</span>
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#49bef5]/40 bg-[#2ba6e0]/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-mono text-[#49bef5]">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#49bef5] shadow-[0_0_6px_rgba(73,190,245,0.8)]" />
                                        AI Report
                                    </span>
                                </div>

                                <div
                                    className="grid sm:grid-flow-col sm:grid-rows-4 sm:grid-cols-2 gap-x-6 gap-y-2"
                                >
                                    {REPORT_SECTIONS.map((r) => (
                                        <div
                                            key={r}
                                            className="flex items-center gap-2.5 text-white/80 text-sm"
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#00e6a0]" />
                                            {r}
                                        </div>
                                    ))}
                                </div>

                                <Link
                                    to="/blog/how-trt-plus-analyzes-your-bloodwork"
                                    data-testid="bloodwork-ai-report-article-link"
                                    className="mt-6 pt-4 border-t border-white/5 inline-flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-[0.18em] text-white/55 hover:text-white transition-colors"
                                >
                                    <span className="text-[#00E6A0]">{">"}</span>
                                    Read the article
                                    <span className="text-[#49BEF5]">→</span>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Phone */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.8 }}
                        className="relative mx-auto lg:mx-0 w-full max-w-[340px] lg:sticky lg:top-28"
                    >
                        <div className="phone-frame">
                            <div className="phone-screen">
                                <img
                                    src={ASSETS.bloodworkScreen}
                                    alt="My Bloodwork"
                                    className="absolute inset-0 w-full h-full object-cover object-top"
                                />
                            </div>
                        </div>
                        {/* chip */}
                        <div className="absolute -right-4 top-[calc(65%-3px)] hidden lg:flex items-center gap-2 bg-black/85 border border-white/10 backdrop-blur-xl rounded-full px-3 py-2 text-xs shadow-lg">
                            <span className="pulse-dot w-2 h-2 rounded-full bg-[#00e6a0]" />
                            <span className="text-white/80 font-mono">
                                Score: 986
                            </span>
                        </div>
                    </motion.div>
                </div>

                {/* Pathology callout */}
                <div className="mt-16 lg:mt-20 tile p-8 lg:p-10 grid lg:grid-cols-[1fr_1.3fr] gap-8 items-center">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest font-mono text-[#49bef5] mb-3">
                            Pathology Partner Integration
                        </div>
                        <h3 className="font-display text-2xl lg:text-3xl font-bold text-white leading-tight">
                            Markers built for{" "}
                            <span className="blue-glow">athletes</span> — not
                            default commercial panels.
                        </h3>
                    </div>
                    <div className="text-white/65 text-[15px] leading-relaxed space-y-3">
                        <p>
                            Standard bloodwork panels weren't designed with
                            athletes or TRT users in mind. Elevated liver
                            markers, low kidney function, thick blood —
                            triggering clinical concern and cost.
                        </p>
                        <p>
                            TRT Plus partners use superior markers where it
                            matters: <b className="text-white">ApoB</b> over
                            HDL/LDL, <b className="text-white">Cystatin C</b>{" "}
                            over eGFR, and <b className="text-white">GGT</b>{" "}
                            over AST/ALT — routinely skewed by training and
                            prone to false positives.
                        </p>
                        <p className="text-white/55">
                            Two panels available —{" "}
                            <b className="text-white">Essential</b> for everyday
                            TRT optimization,{" "}
                            <b className="text-white">Comprehensive</b> for
                            athletes and enhanced protocols. Order directly
                            through the app (region dependent) or BYO bloodwork
                            results.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
